// src/pages/employee/EmployeeMeetings.jsx
// Meetings: employees see TODAY only.
// Join activates 10 mins before meeting time.
// Announcements auto-mark as read.

import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useMeeting } from "../../context/MeetingContext";
import {
  useEmployeeAnnouncements,
  formatDate,
  markAnnouncementRead,
} from "../../hooks/useFirestore";
import {
  getTodaysMeetings,
  getMeetingState,
  isMeetingJoinable,
  isEmployeeVisible,
  formatTimeStr,
} from "../../utils/meetings";
import { EMPLOYEE_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

// ─────────────────────────────────────────────────────────────
// File icon helper
// ─────────────────────────────────────────────────────────────
function fileIcon(name = "") {
  if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name)) return "🖼️";
  if (/\.pdf$/i.test(name)) return "📄";
  if (/\.(doc|docx)$/i.test(name)) return "📝";
  if (/\.(xls|xlsx)$/i.test(name)) return "📊";
  return "📎";
}

// ─────────────────────────────────────────────────────────────
// Employee Meetings
// ─────────────────────────────────────────────────────────────
export function EmployeeMeetings() {
  const { userProfile } = useAuth();

  const { allTodayMeetings, todayMeetingsLoading, joinMeeting, leaveMeeting } =
    useMeeting();

  const [listTab, setListTab] = useState("today");
  const [joining, setJoining] = useState({});
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 10000);

    return () => clearInterval(timer);
  }, []);

  // Categories
  const todayActive = getTodaysMeetings(allTodayMeetings, userProfile);
  const completedToday = (allTodayMeetings || []).filter(
    (m) => isEmployeeVisible(m, userProfile) && getMeetingState(m, userProfile) === "COMPLETED"
  );
  const missedToday = (allTodayMeetings || []).filter(
    (m) => isEmployeeVisible(m, userProfile) && getMeetingState(m, userProfile) === "MISSED"
  );

  const displayedMeetings =
    listTab === "today"
      ? todayActive
      : listTab === "completed"
        ? completedToday
        : missedToday;

  async function handleJoin(meeting) {
    if (!userProfile?.uid) return;

    setJoining((p) => ({
      ...p,
      [meeting.id]: true,
    }));

    try {
      await joinMeeting(meeting.id, userProfile.uid);

      if (meeting.meetLink) {
        window.open(meeting.meetLink, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("[EmployeeMeetings] join:", err);
    }

    setJoining((p) => ({
      ...p,
      [meeting.id]: false,
    }));
  }

  async function handleLeave() {
    if (!userProfile?.uid) return;

    await leaveMeeting(userProfile.uid);
  }

  const isInThisMeeting = (id) =>
    userProfile?.inMeeting && userProfile?.currentMeetingId === id;

  return (
    <Layout navItems={EMPLOYEE_NAV} panelLabel="My Portal">
      <div className="page-header" style={{ marginBottom: "1rem" }}>
        <div>
          <h1 className="page-title">Meetings</h1>

          <p className="page-subtitle">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>

        <div className="meetings-tab-toggle" style={{ display: "flex", gap: "0.5rem" }}>
          <button
            className={`meetings-tab-btn ${listTab === "today" ? "meetings-tab-btn--active" : ""}`}
            onClick={() => setListTab("today")}
          >
            Today's Meetings ({todayActive.length})
          </button>
          <button
            className={`meetings-tab-btn ${listTab === "completed" ? "meetings-tab-btn--active" : ""}`}
            onClick={() => setListTab("completed")}
          >
            Completed ({completedToday.length})
          </button>
          <button
            className={`meetings-tab-btn ${listTab === "missed" ? "meetings-tab-btn--active" : ""}`}
            onClick={() => setListTab("missed")}
          >
            Missed ({missedToday.length})
          </button>
        </div>
      </div>

      {todayMeetingsLoading ? (
        <div className="card-loading">Loading…</div>
      ) : displayedMeetings.length === 0 ? (
        <div className="card-empty" style={{ marginTop: "2rem" }}>
          <div
            style={{
              marginBottom: "0.5rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="32"
              height="32"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
          </div>
          {listTab === "today"
            ? "No active meetings scheduled for today."
            : listTab === "completed"
              ? "No completed meetings today."
              : "No missed meetings today."}
        </div>
      ) : (
        <div className="meetings-full-list">
          {displayedMeetings.map((m) => {
            const state = getMeetingState(m, userProfile);
            const inThis = isInThisMeeting(m.id);
            const inOther = userProfile?.inMeeting && !inThis;

            return (
              <div
                key={m.id}
                className={[
                  "meeting-card",
                  inThis ? "meeting-card--active" : "",
                  (state === "COMPLETED" || state === "MISSED") ? "meeting-card--expired" : "",
                ].join(" ")}
              >
                <div className="meeting-card-header">
                  <span className="meeting-card-title">{m.title}</span>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.4rem",
                      alignItems: "center",
                    }}
                  >
                    {state === "COMPLETED" ? (
                      <span className="status-pill status-pill--gray">
                        Completed
                      </span>
                    ) : state === "MISSED" ? (
                      <span className="status-pill status-pill--red" style={{ backgroundColor: "#ef4444", color: "#fff" }}>
                        Missed
                      </span>
                    ) : state === "LIVE" ? (
                      <span className="status-pill status-pill--green">
                        Live
                      </span>
                    ) : (
                      <span className="status-pill status-pill--orange" style={{ backgroundColor: "#f97316", color: "#fff" }}>
                        Upcoming
                      </span>
                    )}

                    <span className="status-pill status-pill--blue">
                      {m.audience}
                    </span>
                  </div>
                </div>

                <div className="meeting-card-meta">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      width="14"
                      height="14"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      />
                    </svg>
                    {m.date}

                    <span>·</span>

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      width="14"
                      height="14"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>

                    {formatTimeStr(m.startTime || m.time)}
                    {m.endTime && ` - ${formatTimeStr(m.endTime)}`}
                    {(m.durationMinutes || m.duration) ? ` (${m.durationMinutes || m.duration} mins)` : ""}
                  </span>

                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      width="14"
                      height="14"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                      />
                    </svg>

                    {m.scheduledByName}
                  </span>
                </div>

                {m.agenda && <p className="meeting-card-agenda">{m.agenda}</p>}

                <div className="meeting-card-actions">
                  {(() => {
                    // COMPLETED
                    if (state === "COMPLETED") {
                      return (
                        <span
                          className="meeting-link-expired"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            width="14"
                            height="14"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                            />
                          </svg>
                          Meeting Ended
                        </span>
                      );
                    }

                    // MISSED
                    if (state === "MISSED") {
                      return (
                        <span
                          className="meeting-link-expired"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#ef4444"
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            width="14"
                            height="14"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Meeting Missed
                        </span>
                      );
                    }

                    // CURRENT ACTIVE
                    if (inThis) {
                      return (
                        <div className="meeting-active-row">
                          <span className="meeting-active-indicator">
                            <span className="meeting-active-pulse" />
                            You're in this meeting
                          </span>

                          <button
                            className="btn-leave-meeting"
                            onClick={handleLeave}
                          >
                            Leave Meeting
                          </button>
                        </div>
                      );
                    }

                    // UPCOMING
                    if (state === "UPCOMING" && !isMeetingJoinable(m)) {
                      const start = new Date(`${m.date}T${m.time}`);
                      const activationTime = new Date(start.getTime() - 10 * 60 * 1000);
                      return (
                        <button
                          className="btn-join-meeting"
                          disabled={true}
                          style={{
                            opacity: 0.7,
                          }}
                        >
                          Available 10 mins before ({activationTime.toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })})
                        </button>
                      );
                    }

                    // LIVE
                    return m.meetLink ? (
                      <button
                        className="btn-join-meeting"
                        onClick={() => handleJoin(m)}
                        disabled={joining[m.id] || inOther}
                        title={
                          inOther ? "You're already in another meeting" : ""
                        }
                      >
                        {joining[m.id] ? "Joining…" : "Join Meeting →"}
                      </button>
                    ) : (
                      <span
                        style={{
                          fontSize: "0.78rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        No meeting link
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* INFO NOTE */}

    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Employee Announcements
// ─────────────────────────────────────────────────────────────
export function EmployeeAnnouncements() {
  const { userProfile } = useAuth();

  const { data: announcements, loading } = useEmployeeAnnouncements(userProfile?.teamId);

  const priorityIcon = {
    high: "🔴",
    medium: "🟡",
    low: "🟢",
  };

  useEffect(() => {
    if (!userProfile?.uid || announcements.length === 0) return;

    const uid = userProfile.uid;

    announcements.forEach((a) => {
      if (!(a.readBy || []).includes(uid)) {
        markAnnouncementRead(a.id, uid).catch(console.error);
      }
    });
  }, [announcements, userProfile?.uid]);

  return (
    <Layout navItems={EMPLOYEE_NAV} panelLabel="My Portal">
      <div className="page-header">
        <h1 className="page-title">Announcements</h1>
      </div>

      {loading ? (
        <div className="card-loading">Loading…</div>
      ) : announcements.length === 0 ? (
        <div className="card-empty">No announcements yet.</div>
      ) : (
        <div className="announcements-full-list">
          {announcements.map((a) => (
            <div
              key={a.id}
              className={`announcement-card priority-${a.priority}`}
            >
              <div className="announcement-card-header">
                <span>
                  {priorityIcon[a.priority]} {a.title}
                </span>

                <span className="status-pill status-pill--gray">
                  {a.audience}
                </span>
              </div>

              <p className="announcement-card-msg">{a.message}</p>

              {Array.isArray(a.attachments) && a.attachments.length > 0 && (
                <div className="attachment-list">
                  {a.attachments.map((att, i) => (
                    <a
                      key={i}
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className={
                        att.type?.startsWith("image/")
                          ? "attachment-image-link"
                          : "attachment-doc-chip"
                      }
                    >
                      {att.type?.startsWith("image/") ? (
                        <img
                          src={att.url}
                          alt={att.name || "attachment"}
                          className="attachment-thumb"
                        />
                      ) : (
                        <>
                          {fileIcon(att.name)} {att.name}
                        </>
                      )}
                    </a>
                  ))}
                </div>
              )}

              <div className="announcement-card-meta">
                <span>By {a.postedByName} {a.teamName ? `(${a.teamName})` : ""}</span>

                <span>{formatDate(a.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}

export default EmployeeMeetings;
